import type { PageDetail, WorkspaceSummary } from '../api';
import { t } from '../i18n';
import type { ThemePreference } from '../theme';
import {
  ActionMenu,
  ActionMenuCheckboxItem,
  ActionMenuItem,
  ActionMenuRadioGroup,
  ActionMenuRadioItem,
} from './ActionMenu';
import { Glyph, ICON } from './icons';

const THEME_MODES = ['system', 'light', 'dark'] as const;

interface WorkspaceMenuProps {
  workspace: WorkspaceSummary;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onInvite: () => void;
  onCreatePage: () => void;
  onSettings: () => void;
  onCopyLink: () => void;
}

export function WorkspaceMenu({
  workspace,
  open,
  onOpenChange,
  onInvite,
  onCreatePage,
  onSettings,
  onCopyLink,
}: WorkspaceMenuProps): React.ReactElement {
  return (
    <ActionMenu
      open={open}
      onOpenChange={onOpenChange}
      label={t('sidebar.wsOptions')}
      className="menu--wide"
      align="start"
      trigger={
        <button
          type="button"
          className="iconbtn"
          aria-label={t('sidebar.wsOptions')}
          title={t('sidebar.wsOptions')}
        >
          <Glyph d={ICON.more} />
        </button>
      }
    >
      <div className="menu__head">
        {workspace.name} ·{' '}
        {workspace.memberCount === 1
          ? t('sidebar.onlyYou')
          : t('sidebar.memberCount', { count: String(workspace.memberCount) })}
      </div>
      <ActionMenuItem transfersFocus onSelect={onInvite}>
        <Glyph d={ICON.userPlus} />
        {t('sidebar.inviteMembers')}
      </ActionMenuItem>
      <ActionMenuItem onSelect={onCreatePage}>
        <Glyph d={ICON.plus} />
        {t('workspace.newPage')}
      </ActionMenuItem>
      <ActionMenuItem onSelect={onSettings}>
        <Glyph d={ICON.sliders} />
        {t('sidebar.wsSettings')}
      </ActionMenuItem>
      <ActionMenuItem onSelect={onCopyLink}>
        <Glyph d={ICON.link} />
        {t('sidebar.copyLink')}
      </ActionMenuItem>
    </ActionMenu>
  );
}

export type DocumentPatch = {
  isLocked?: boolean;
  font?: 0 | 1 | 2;
  small?: boolean;
  wide?: boolean;
};

interface DocumentMenuProps {
  page: PageDetail;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onPatch: (patch: DocumentPatch) => Promise<void>;
  onToggleFavorite: () => void;
  onDuplicate: () => void;
  onTrash: () => void;
  onAddComment: () => void;
  onUndo: () => void;
  onRedo: () => void;
  onImport: () => void;
  onExport: () => void;
}

function fontLabel(font: PageDetail['font']): string {
  if (font === 1) return t('page.font.serif');
  if (font === 2) return t('page.font.mono');
  return t('page.font.default');
}

function nextFont(font: PageDetail['font']): PageDetail['font'] {
  if (font === 0) return 1;
  return font === 1 ? 2 : 0;
}

export function DocumentMenu({
  page,
  open,
  onOpenChange,
  onPatch,
  onToggleFavorite,
  onDuplicate,
  onTrash,
  onAddComment,
  onUndo,
  onRedo,
  onImport,
  onExport,
}: DocumentMenuProps): React.ReactElement {
  return (
    <ActionMenu
      open={open}
      onOpenChange={onOpenChange}
      label={t('sidebar.pageOptions')}
      className="menu--topbar"
      trigger={
        <button
          type="button"
          className="iconbtn topbar__more"
          aria-label={t('sidebar.pageOptions')}
        >
          <Glyph d={ICON.more} size={18} />
        </button>
      }
    >
      <ActionMenuItem transfersFocus onSelect={onAddComment}>
        {t('page.menu.addComment')}
      </ActionMenuItem>
      <ActionMenuItem onSelect={onToggleFavorite}>
        {page.favorite ? t('page.menu.unfavorite') : t('page.menu.favorite')}
      </ActionMenuItem>
      <ActionMenuItem keepOpen onSelect={() => void onPatch({ font: nextFont(page.font) })}>
        {t('page.menu.font', { name: fontLabel(page.font) })}
      </ActionMenuItem>
      <ActionMenuCheckboxItem
        checked={page.small}
        onCheckedChange={(small) => void onPatch({ small })}
      >
        {page.small ? '✓ ' : ''}
        {t('page.menu.small')}
      </ActionMenuCheckboxItem>
      <ActionMenuCheckboxItem
        checked={page.wide}
        onCheckedChange={(wide) => void onPatch({ wide })}
      >
        {page.wide ? '✓ ' : ''}
        {t('page.menu.wide')}
      </ActionMenuCheckboxItem>
      <ActionMenuItem onSelect={() => void onPatch({ isLocked: !page.isLocked })}>
        {page.isLocked ? t('page.menu.unlock') : t('page.menu.lock')}
      </ActionMenuItem>
      <ActionMenuItem onSelect={onDuplicate}>{t('page.menu.duplicate')}</ActionMenuItem>
      <ActionMenuItem transfersFocus onSelect={onImport}>
        {t('page.menu.import')}
      </ActionMenuItem>
      <ActionMenuItem transfersFocus onSelect={onExport}>
        {t('page.menu.export')}
      </ActionMenuItem>
      <ActionMenuItem onSelect={onUndo}>{t('page.menu.undo')}</ActionMenuItem>
      <ActionMenuItem onSelect={onRedo}>{t('page.menu.redo')}</ActionMenuItem>
      <ActionMenuItem danger onSelect={onTrash}>
        {t('page.menu.trash')}
      </ActionMenuItem>
    </ActionMenu>
  );
}

interface AccountMenuProps {
  email: string;
  trigger: React.ReactElement;
  preference: ThemePreference;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onPreferenceChange: (preference: ThemePreference) => void;
  onSignOut: () => void;
  rootClassName?: string;
}

export function AccountMenu({
  email,
  trigger,
  preference,
  open,
  onOpenChange,
  onPreferenceChange,
  onSignOut,
  rootClassName,
}: AccountMenuProps): React.ReactElement {
  return (
    <ActionMenu
      open={open}
      onOpenChange={onOpenChange}
      label={t('sidebar.account')}
      className="menu--account"
      side="top"
      align="start"
      rootClassName={rootClassName}
      trigger={trigger}
    >
      <div className="menu__head">{email}</div>
      <ActionMenuRadioGroup
        value={preference}
        onValueChange={(value) => {
          const mode = THEME_MODES.find((candidate) => candidate === value);
          if (mode) onPreferenceChange(mode);
        }}
      >
        {THEME_MODES.map((mode) => (
          <ActionMenuRadioItem key={mode} value={mode}>
            <span className="menu__mark" aria-hidden="true">
              {preference === mode ? '✓' : ''}
            </span>
            {t(`theme.${mode}`)}
          </ActionMenuRadioItem>
        ))}
      </ActionMenuRadioGroup>
      <div className="menu__sep" />
      <ActionMenuItem
        onSelect={() => {
          window.location.hash = '#legal/privacy';
        }}
      >
        <span className="menu__mark" aria-hidden="true" />
        {t('legal.privacy.title')}
      </ActionMenuItem>
      <ActionMenuItem
        onSelect={() => {
          window.location.hash = '#legal/terms';
        }}
      >
        <span className="menu__mark" aria-hidden="true" />
        {t('legal.terms.title')}
      </ActionMenuItem>
      <div className="menu__sep" />
      <ActionMenuItem onSelect={onSignOut}>
        <span className="menu__mark" aria-hidden="true" />
        {t('workspace.signOut')}
      </ActionMenuItem>
    </ActionMenu>
  );
}
